"use client";

import React, { useState, useMemo } from "react";
import { Plus, Search, BookOpen, FileText } from "lucide-react";
import { Input, Button, Card, CardContent, CardHeader } from "@west-tennessee-consulting/ui";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { useGetMspDocuments } from "@/lib/hooks/api/msp-docs/msp-docs-graphql";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import type { Msp_DocumentsFilter } from "@/src/gql/graphql";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { MspDocumentEditor } from "@/features/msp-docs/components/MspDocumentEditor";
import { MspDocumentCard } from "@/features/msp-docs/components/MspDocumentCard";
import {
  MSP_DOCUMENT_CATEGORIES,
  MSP_DOCUMENT_DIFFICULTIES,
} from "@/features/msp-docs/components/msp-document-constants";
import { Client } from "@/lib/types/database";

const filterCategories = [
  { value: "", label: "All Categories" },
  ...MSP_DOCUMENT_CATEGORIES,
] as Array<{ value: string; label: string }>;

const filterDifficulties = [
  { value: "", label: "All Levels" },
  ...MSP_DOCUMENT_DIFFICULTIES,
] as Array<{ value: string; label: string }>;

interface ContactDocumentationProps {
  client: Client;
  isCompanyView?: boolean;
  companyId?: string;
}

export function ContactDocumentation({
  client,
  isCompanyView = false,
  companyId,
}: ContactDocumentationProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { isAdmin, isEmployee } = useRoleCheck();

  const graphqlFilters = useMemo(() => {
    const filters: Msp_DocumentsFilter = {
      is_active: { eq: true },
    };

    if (isCompanyView) {
      filters.company_id = { eq: companyId || client.id };
    } else {
      filters.client_id = { eq: client.id };
    }

    if (category) filters.category = { eq: category };
    if (difficulty) filters.difficulty = { eq: difficulty };
    if (searchTerm) filters.title = { ilike: `%${searchTerm}%` };

    return filters;
  }, [client.id, companyId, isCompanyView, category, difficulty, searchTerm]);

  const {
    data: documentsData,
    isLoading,
    error,
  } = useGetMspDocuments({
    first: 20,
    filter: graphqlFilters,
  });

  const documents = documentsData?.msp_documentsCollection?.edges?.map((e) => e.node) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">
            {isCompanyView ? "Company Documentation" : "Client Documentation"}
          </h3>
          <p className="text-sm text-muted-foreground">
            Documentation specific to {client.company_name || client.name}
          </p>
        </div>
        {(isAdmin || isEmployee) && (
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 px-2 py-1 rounded border border-blue-200 dark:border-blue-800">
              <div className="font-medium text-blue-900 dark:text-blue-100">Required:</div>
              <div className="text-blue-800 dark:text-blue-200">Title, Content, Category</div>
              <div className="text-blue-600 dark:text-blue-400 text-xs">
                {isCompanyView ? "Company auto-assigned" : "Client auto-assigned"}
              </div>
            </div>
            <Button onClick={() => setIsCreateModalOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Create Document
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder={`Search ${isCompanyView ? "company" : "client"} documentation...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <ResponsiveCommandSelect
                  value={category}
                  onValueChange={setCategory}
                  placeholder="All Categories"
                  options={filterCategories}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Difficulty</label>
                <ResponsiveCommandSelect
                  value={difficulty}
                  onValueChange={setDifficulty}
                  placeholder="All Levels"
                  options={filterDifficulties}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-3 bg-muted rounded"></div>
                  <div className="h-3 bg-muted rounded w-5/6"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error Loading Documents</h3>
              <p className="text-muted-foreground">
                There was an error loading the documentation. Please try again.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : documents.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                No {isCompanyView ? "Company" : "Client"} Documentation
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || category || difficulty
                  ? "No documents match your search criteria."
                  : `No documentation has been created for this ${isCompanyView ? "company" : "client"} yet.`}
              </p>
              {isAdmin && !searchTerm && !category && !difficulty && (
                <Button onClick={() => setIsCreateModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Document
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {documents.map((doc) => (
            <MspDocumentCard key={doc.id} doc={doc} />
          ))}
        </div>
      )}

      <ResponsiveModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        title={`Create ${isCompanyView ? "Company" : "Client"} Document`}
        maxWidth="4xl"
      >
        <MspDocumentEditor
          onSuccess={() => setIsCreateModalOpen(false)}
          onCancel={() => setIsCreateModalOpen(false)}
          defaultValues={{
            ...(isCompanyView ? { company_id: companyId || client.id } : { client_id: client.id }),
            category: "client-specific" as const,
          }}
        />
      </ResponsiveModal>
    </div>
  );
}

export { ContactDocumentation as ClientDocumentation };
